// Run Assessment / Publish Training, step 1: Details. One input beneath the
// next, each with its name above it and a hairline between: Name, Stage,
// Program Type, Audience (roles, optionally narrowed by attributes), Owner &
// Timing, Knowledge. Each input is its own
// component, so Review can open any one of them on its own to be edited.

import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { useProgramContext } from '../../../../../hooks/useProgramContext';
import { Bone } from '../../../../../components/ui/DashboardSkeletons';
import { useOrgAudienceOptions } from '../../Program/components/learning-lifecycle/useOrgMemberAttributes';
import type { ProgramTypeKey } from '../../Program/components/learning-lifecycle/types';
import AddFileModal from '../../aimd-v2/components/AddFileModal';
import AddTextModal from '../../aimd-v2/components/AddTextModal';
import AddUrlModal from '../../aimd-v2/components/AddUrlModal';
import { getProcessingStatus, uploadFileDocument, uploadTextDocument, uploadUrlDocument } from '../../aimd-v2/api';
import type { DocumentItem } from '../../aimd-v2/types';
import { withCountedCapabilities, type SetupRole } from '../capabilitySetup';
import { isRoleUsable, type CapabilitySet } from '../sharedSets';
import { assessmentRolesNote, rolesNoteForType } from './trainingRules';
import {
  ActionButton,
  BODY,
  BRANDY,
  BRANDY_FILL,
  Chip,
  ChipLine,
  ChoiceCard,
  DateField,
  FONT,
  FormRow,
  INPUT_SX,
  LABEL,
  LINE,
  Note,
  RowGroupLabel,
  SCROLL,
  Segmented,
  SelectField,
  TEXT,
  TEXT_2,
  TEXT_3,
  TEXT_4,
  WELL
} from '../designSystem';
import { docsCache, prefetchKnowledge } from './knowledgeCache';
import { groupsMissing, type AudienceGroupsDraft } from './audienceGroups';
import type { AudienceGroupsRefusal } from '../../../../../api/audienceRequirements';
import { AudienceGroupsField } from './AudienceGroupsField';
import { useRequirementsPilot } from '../../Program/components/program tabs/ProgramSettings/requirements/useRequirements';

export type FlowKind = 'assessment' | 'training';

/** What the Details step collects. */
export interface FlowDetails {
  name: string;
  docIds: string[];
  stageId: string | null;
  category: ProgramTypeKey | null;
  /** Roles from Capability Setup. */
  roleIds: string[];
  /** Attribute values (a team is one of these). */
  audienceIds: string[];
  audienceMatch: Record<string, 'any' | 'all'>;
  allLearners: boolean;
  /** Read & Assess and Read Only (prototype: The Playground, development builds). */
  groups?: AudienceGroupsDraft;
  publishBy: Date | null;
  ownerId: string | null;
}

export const EMPTY_DETAILS: FlowDetails = {
  name: '',
  docIds: [],
  stageId: null,
  category: null,
  roleIds: [],
  audienceIds: [],
  audienceMatch: {},
  allLearners: false,
  publishBy: null,
  ownerId: null
};

export type SetDetail = <K extends keyof FlowDetails>(key: K, value: FlowDetails[K]) => void;

export interface DetailsFieldProps {
  type: FlowKind;
  details: FlowDetails;
  set: SetDetail;
  pillars: { id: string; name: string }[];
  members: { id: string; name: string }[];
  /** Every role set up in Capability Setup, across teams. */
  roles: SetupRole[];
  /** The attribute list the organization uses as its Teams (Settings). */
  teamsListId: string | null;
  /** The organization's shared capabilities (Development, Standards), applied to roles. */
  sets?: CapabilitySet[];
  /**
   * The backend's refusal of the audience groups last sent (Audience Requirements, with its
   * backend on): its message, and the pair of groups it found someone could be in both of.
   */
  groupsRefusal?: AudienceGroupsRefusal | null;
}

/**
 * The three program types, each with what it is, who it can be for and how it
 * uses capabilities. Every type is for roles, and builds the capabilities of
 * its own source that the roles share: Role Excellence the roles' own,
 * Development and Standards the organization's shared ones applied to them.
 */
const CATEGORIES: { key: ProgramTypeKey; title: string; body: string }[] = [
  {
    key: 'role-excellence',
    title: 'Role Excellence',
    body: "The purpose: Doing a specific job well.\nFor: Specific roles.\nElliott builds: Lessons and a Scored Roleplay for each of the roles' own capabilities they share."
  },
  {
    key: 'development',
    title: 'Development',
    body: 'The purpose: Growing people beyond their current role.\nFor: Specific roles.\nElliott builds: Lessons and a Scored Roleplay for each Development capability the roles share.'
  },
  {
    key: 'standards',
    title: 'Standards',
    body: 'The purpose: Compliance and conduct everyone must meet.\nFor: Specific roles.\nElliott builds: Lessons and a Scored Roleplay for each Standards capability the roles share.'
  }
];
export const CATEGORY_NAME: Record<string, string> = Object.fromEntries(CATEGORIES.map((c) => [c.key, c.title]));
const NOTE = { ...BODY, fontSize: 12.5, color: TEXT_3, py: '6px' };

/** "ONBOARDING" or "role excellence" as "Onboarding", "Role Excellence": a capital, then lower case, per word. */
export const capitalized = (text: string) =>
  text
    .toLowerCase()
    .replace(/(^|[\s\-/&(])([a-z])/g, (_m, lead: string, letter: string) => lead + letter.toUpperCase());

const toggled = (list: string[], id: string) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

// ---------------------------------------------------------------------------
// Name, Stage, Category
// ---------------------------------------------------------------------------

export const NameField: React.FC<DetailsFieldProps> = ({ details, set }) => (
  <FormRow required label="Name">
    <Box
      component="input"
      autoFocus
      value={details.name}
      aria-label="Program name"
      placeholder="e.g. Sales Excellence"
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => set('name', e.target.value)}
      sx={INPUT_SX}
    />
  </FormRow>
);

export const StageField: React.FC<DetailsFieldProps> = ({ details, set, pillars }) => (
  <FormRow required label="Stage" tip="The column it sits in on your Capability Dashboard.">
    <ChipLine>
      {pillars.map((p) => (
        <Chip
          key={p.id}
          label={capitalized(p.name)}
          selected={details.stageId === p.id}
          onClick={() => set('stageId', p.id)}
        />
      ))}
      {pillars.length === 0 && (
        <Box role="status" aria-label="Loading your stages" sx={{ display: 'flex', gap: '8px' }}>
          {[96, 120, 88].map((w) => (
            <Bone key={w} width={w} height={32} radius="4px" />
          ))}
        </Box>
      )}
    </ChipLine>
  </FormRow>
);

/**
 * Program Type: each choice spelled out on the page, with who it can be for and
 * how it uses capabilities. An Assessment always measures capabilities, so it
 * is always Role Excellence and has nothing to choose. Every type is for roles,
 * so the audience stands whatever the type.
 */
export const CategoryField: React.FC<DetailsFieldProps> = ({ type, details, set }) => {
  if (type === 'assessment') return null;
  const choose = (key: ProgramTypeKey) => {
    if (key !== details.category) set('category', key);
  };
  return (
    <FormRow required label="Category">
      <Box role="radiogroup" aria-label="Program type" sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {CATEGORIES.map((c) => (
          <ChoiceCard
            key={c.key}
            title={c.title}
            body={c.body}
            selected={details.category === c.key}
            onClick={() => choose(c.key)}
          />
        ))}
      </Box>
    </FormRow>
  );
};

// ---------------------------------------------------------------------------
// Audience: Team, Role, the other attribute lists, Everyone
// ---------------------------------------------------------------------------

/** One line of the audience group: what it is matched on, then its options on a line that scrolls. */
const AudienceLine: React.FC<{
  label: string;
  aside?: React.ReactNode;
  first?: boolean;
  children: React.ReactNode;
}> = ({ label, aside, first = false, children }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      py: '12px',
      ...(first ? { pt: 0 } : { borderTop: `1px solid ${LINE}` })
    }}
  >
    <RowGroupLabel aside={aside}>{label}</RowGroupLabel>
    {children}
  </Box>
);

/** A text action at the end of a line (Select All, Clear). */
export const LineAction: React.FC<{ children: React.ReactNode; onClick: () => void }> = ({ children, onClick }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      p: 0,
      border: 'none',
      bgcolor: 'transparent',
      color: TEXT_2,
      fontFamily: FONT,
      fontSize: 12,
      fontWeight: 600,
      whiteSpace: 'nowrap',
      cursor: 'pointer',
      '&:hover': { color: BRANDY }
    }}
  >
    {children}
  </Box>
);

/**
 * Who the program is for: roles, team by team (every program and every
 * Assessment is for roles), with a note saying which capabilities the choice
 * leaves to build. A Training can be narrowed by the organization's other
 * attribute lists (all must match). Until a type is chosen there is nothing
 * to choose from.
 */
export const AudienceField: React.FC<DetailsFieldProps> = (props) => {
  const { type, details, set, roles, teamsListId, sets = [] } = props;
  // A role can be chosen once it can be built on: confirmed by its manager, with something counting on it.
  const usable = (r: SetupRole) => isRoleUsable(r, sets);
  // A program for roles can be narrowed by the other attribute lists (all must match).
  const [narrowing, setNarrowing] = useState(details.audienceIds.length > 0);
  const { catalog } = useOrgAudienceOptions();
  const teamList =
    catalog.find((list) => list._id === teamsListId) ??
    catalog.find((list) => /^(teams?|departments?)$/i.test(list.name.trim())) ??
    null;
  const otherLists = catalog.filter((list) => list._id !== teamList?._id);
  const groupsOn = useRequirementsPilot();

  if (type === 'training' && !details.category) {
    return (
      <FormRow required label="Audience">
        <Typography sx={NOTE}>Choose a category first. The category decides who this program can be for.</Typography>
      </FormRow>
    );
  }

  // A Training's audience as two groups: Read & Assess, and Read Only (prototype).
  if (type === 'training' && groupsOn) {
    return <AudienceGroupsField {...props} />;
  }

  // Roles, under the team each belongs to: two teams can each have a role of the same name.
  const teamName = (id: string) => teamList?.values.find((v) => v._id === id)?.value ?? 'Other';
  const teamIds = Array.from(new Set(roles.map((r) => r.teamId)));
  // Each with what counts on it, for the note: a capability added since its levels were confirmed waits.
  const chosen = roles.filter((r) => details.roleIds.includes(r.id) && usable(r)).map(withCountedCapabilities);
  const allReady = roles.filter(usable).map((r) => r.id);
  const everyRole = allReady.length > 0 && allReady.every((id) => details.roleIds.includes(id));
  return (
    <FormRow
      required
      label="Audience"
      tip={
        type === 'assessment'
          ? 'The roles to assess. An Assessment measures capabilities, so it is for roles only.'
          : 'The roles this program is for. Every program is for roles: whoever holds one, now or later, is in it. Narrow it down by other attributes if you need to.'
      }
      aside={
        allReady.length > 1 && (
          <LineAction
            onClick={() =>
              set('roleIds', everyRole ? details.roleIds.filter((id) => !allReady.includes(id)) : allReady)
            }
          >
            {everyRole ? 'Clear All Roles' : 'All Roles'}
          </LineAction>
        )
      }
    >
      <Box>
        {roles.length === 0 && <Typography sx={NOTE}>No roles yet. Add them in Capabilities.</Typography>}
        {teamIds.map((teamId, i) => {
          const teamRoles = roles.filter((r) => r.teamId === teamId);
          const ready = teamRoles.filter(usable).map((r) => r.id);
          const allOn = ready.length > 0 && ready.every((id) => details.roleIds.includes(id));
          return (
            <AudienceLine
              key={teamId}
              first={i === 0}
              label={teamName(teamId)}
              aside={
                ready.length > 1 && (
                  <LineAction
                    onClick={() =>
                      set(
                        'roleIds',
                        allOn
                          ? details.roleIds.filter((id) => !ready.includes(id))
                          : Array.from(new Set([...details.roleIds, ...ready]))
                      )
                    }
                  >
                    {allOn ? 'Clear' : 'Select All'}
                  </LineAction>
                )
              }
            >
              <ChipLine>
                {teamRoles.map((r) => (
                  <Chip
                    key={r.id}
                    multi
                    label={r.name}
                    selected={details.roleIds.includes(r.id)}
                    disabled={!usable(r)}
                    reason="Confirm its capabilities and levels in Capabilities first."
                    onClick={() => set('roleIds', toggled(details.roleIds, r.id))}
                  />
                ))}
              </ChipLine>
            </AudienceLine>
          );
        })}
        {/* What the roles chosen leave to build (a Training) or to answer (an Assessment). */}
        {roles.length > 0 && (
          <Box sx={{ pt: '12px', borderTop: `1px solid ${LINE}` }}>
            <Note>
              {type === 'training' ? rolesNoteForType(details.category, chosen, sets) : assessmentRolesNote(chosen)}
            </Note>
          </Box>
        )}
        {/* Narrowing a program for roles by the organization's other lists: optional, all must match. */}
        {type === 'training' && otherLists.length > 0 && (
          <Box sx={{ pt: '12px', mt: '12px', borderTop: `1px solid ${LINE}` }}>
            <LineAction onClick={() => setNarrowing((v) => !v)}>
              {narrowing ? 'Hide Narrow By Attributes' : 'Narrow By Attributes (Optional)'}
            </LineAction>
            {narrowing &&
              otherLists.map((list, i) => (
                <AudienceLine
                  key={list._id}
                  first={i === 0}
                  label={list.name}
                  aside={
                    <Segmented
                      small
                      value={details.audienceMatch[list._id] ?? 'any'}
                      onChange={(m) => set('audienceMatch', { ...details.audienceMatch, [list._id]: m })}
                      options={[
                        { value: 'any', label: 'Match Any' },
                        { value: 'all', label: 'Match All' }
                      ]}
                    />
                  }
                >
                  <ChipLine>
                    {list.values.map((v) => (
                      <Chip
                        key={v._id}
                        multi
                        label={v.value}
                        selected={details.audienceIds.includes(v._id)}
                        onClick={() => set('audienceIds', toggled(details.audienceIds, v._id))}
                      />
                    ))}
                  </ChipLine>
                </AudienceLine>
              ))}
          </Box>
        )}
      </Box>
    </FormRow>
  );
};

// ---------------------------------------------------------------------------
// Knowledge: choose from the Knowledge Hub, or add a file, a URL or text
// ---------------------------------------------------------------------------

const isProcessing = (d: DocumentItem) => d.processingStatus === 'processing' || d.processingStatus === 'uploaded';
/** The same sources, with the latest status of any still processing. */
const refreshOne = async (d: DocumentItem): Promise<DocumentItem> =>
  isProcessing(d) ? { ...d, processingStatus: await getProcessingStatus(d.id).catch(() => d.processingStatus) } : d;
const refreshStatuses = (docs: DocumentItem[]) => Promise.all(docs.map(refreshOne));

export const KnowledgeField: React.FC<DetailsFieldProps> = ({ type, details, set }) => {
  const selected = details.docIds;
  const onChange = (ids: string[]) => set('docIds', ids);
  const { groupId } = useProgramContext();
  const [docs, setDocs] = useState<DocumentItem[] | null>(() => (groupId ? docsCache.get(groupId) ?? null : []));
  const [added, setAdded] = useState<DocumentItem[]>([]);
  const [modal, setModal] = useState<'file' | 'url' | 'text' | null>(null);
  const [uploading, setUploading] = useState(false);
  const selectedRef = useRef(selected);
  selectedRef.current = selected;

  useEffect(() => {
    let live = true;
    prefetchKnowledge(groupId).then((d) => live && setDocs(d));
    return () => {
      live = false;
    };
  }, [groupId]);

  // A new source processes in the background; check on it until it is ready.
  useEffect(() => {
    if (!added.some(isProcessing)) return undefined;
    const id = window.setInterval(() => refreshStatuses(added).then(setAdded), 5000);
    return () => window.clearInterval(id);
  }, [added]);

  const addDoc = async (make: () => Promise<DocumentItem>) => {
    setUploading(true);
    try {
      const doc = await make();
      setAdded((a) => [...a, doc]);
      onChange([...selectedRef.current, doc.id]);
    } catch {
      /* the upload modal reports its own failure */
    } finally {
      setUploading(false);
    }
  };

  const all = [...added, ...(docs ?? []).filter((d) => !added.some((a) => a.id === d.id))];
  const [query, setQuery] = useState('');
  const term = query.trim().toLowerCase();
  const shown = term ? all.filter((d) => `${d.title} ${d.fileTypeLabel}`.toLowerCase().includes(term)) : all;

  return (
    <FormRow
      label="Knowledge"
      tip={
        type === 'assessment'
          ? 'Sources that show how this role works, such as playbooks or call guides. Elliott uses them to write realistic Roleplays.'
          : 'The sources Elliott should draw on. Specific material, such as playbooks and call guides, gives a program that fits how your team works.'
      }
      aside={
        <Typography sx={{ ...LABEL, color: uploading ? BRANDY : TEXT_3 }}>
          {uploading ? 'Adding Your Source' : `${selected.length} Selected`}
        </Typography>
      }
    >
      {/* The sources as tick rows in a well that scrolls on its own, so a long hub does not push the page. */}
      {all.length > 0 && (
        <Box
          component="input"
          type="search"
          value={query}
          aria-label="Search sources"
          placeholder={`Search ${all.length} sources`}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
          sx={INPUT_SX}
        />
      )}
      {(docs === null || all.length > 0) && (
        <Box sx={{ border: `1px solid ${LINE}`, borderRadius: '4px', bgcolor: WELL, maxHeight: 190, ...SCROLL }}>
          {docs === null && (
            <Box role="status" aria-label="Loading your sources">
              {[0, 1, 2].map((i) => (
                <Box
                  key={i}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    height: 36,
                    px: '12px',
                    ...(i > 0 ? { borderTop: '1px solid #1F1F1F' } : {})
                  }}
                >
                  <Bone width={14} height={14} radius="3px" />
                  <Bone width="55%" height={12} radius="4px" />
                </Box>
              ))}
            </Box>
          )}
          {docs !== null && shown.length === 0 && (
            <Typography sx={{ ...NOTE, px: '12px' }}>No sources match your search.</Typography>
          )}
          {shown.map((d, i) => {
            const on = selected.includes(d.id);
            return (
              <Box
                key={d.id}
                component="button"
                type="button"
                role="checkbox"
                aria-checked={on}
                onClick={() => onChange(toggled(selected, d.id))}
                sx={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  height: 36,
                  px: '12px',
                  border: 'none',
                  ...(i > 0 ? { borderTop: '1px solid #1F1F1F' } : {}),
                  bgcolor: on ? BRANDY_FILL : 'transparent',
                  textAlign: 'left',
                  cursor: 'pointer',
                  transition: 'background-color 0.15s',
                  '&:hover': { bgcolor: on ? BRANDY_FILL : 'rgba(255, 255, 255, 0.03)' }
                }}
              >
                <Box
                  aria-hidden
                  sx={{
                    width: 14,
                    height: 14,
                    flexShrink: 0,
                    borderRadius: '3px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: `1.5px solid ${on ? BRANDY : TEXT_4}`,
                    bgcolor: on ? BRANDY : 'transparent'
                  }}
                >
                  {on && <CheckIcon sx={{ fontSize: 11, color: WELL }} />}
                </Box>
                <Typography
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    fontFamily: FONT,
                    fontSize: 13,
                    color: on ? TEXT : TEXT_2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {d.title || d.fileTypeLabel}
                </Typography>
                <Typography sx={{ ...LABEL, flexShrink: 0, color: isProcessing(d) ? BRANDY : TEXT_3 }}>
                  {isProcessing(d) ? 'Processing' : d.fileTypeLabel}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}
      <ChipLine>
        {(
          [
            ['file', 'Add File'],
            ['url', 'Add URL'],
            ['text', 'Add Text']
          ] as const
        ).map(([kind, label]) => (
          <ActionButton key={kind} disabled={uploading || !groupId} onClick={() => setModal(kind)}>
            {label}
          </ActionButton>
        ))}
      </ChipLine>
      {groupId && (
        <>
          <AddFileModal
            open={modal === 'file'}
            onClose={() => setModal(null)}
            onSubmit={(f, ins) => addDoc(() => uploadFileDocument(f, groupId, ins || undefined))}
          />
          <AddUrlModal
            open={modal === 'url'}
            onClose={() => setModal(null)}
            onSubmit={(title, url, ins) => addDoc(() => uploadUrlDocument(title, url, groupId, ins || undefined))}
          />
          <AddTextModal
            open={modal === 'text'}
            onClose={() => setModal(null)}
            onSubmit={(title, text) => addDoc(() => uploadTextDocument(title, text, groupId))}
          />
        </>
      )}
    </FormRow>
  );
};

// ---------------------------------------------------------------------------
// Owner & Timing
// ---------------------------------------------------------------------------

export const OwnerTimingField: React.FC<DetailsFieldProps> = ({ details, set, members }) => (
  <FormRow label="Owner & Timing" tip="Who owns the program, and (optionally) the date to publish it by.">
    <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '14px' }}>
      {/* Only the owner is required: its own label carries the mark. */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        <Typography sx={{ ...LABEL, color: TEXT_2 }}>
          Owner
          <Box component="span" aria-label="required" sx={{ color: BRANDY }}>
            *
          </Box>
        </Typography>
        <SelectField
          label="Owner"
          options={members}
          value={details.ownerId}
          placeholder="Choose an owner"
          onChange={(id) => set('ownerId', id)}
        />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        <Typography sx={{ ...LABEL, color: TEXT_2 }}>Publish by</Typography>
        <DateField label="Publish by (optional)" value={details.publishBy} onChange={(d) => set('publishBy', d)} />
      </Box>
    </Box>
  </FormRow>
);

// ---------------------------------------------------------------------------
// The step's inputs, and what is still needed
// ---------------------------------------------------------------------------

export type DetailKey = 'name' | 'stage' | 'category' | 'audience' | 'knowledge' | 'owner';

/** Each Details input by key: the step shows them all in this order; Review opens one at a time. */
export const DETAIL_FIELDS: Record<DetailKey, React.FC<DetailsFieldProps>> = {
  name: NameField,
  stage: StageField,
  category: CategoryField,
  audience: AudienceField,
  owner: OwnerTimingField,
  knowledge: KnowledgeField
};

export const DetailsFields: React.FC<DetailsFieldProps> = (props) => (
  <>
    {(Object.keys(DETAIL_FIELDS) as DetailKey[]).map((key) => {
      const FieldComponent = DETAIL_FIELDS[key];
      return <FieldComponent key={key} {...props} />;
    })}
  </>
);

/** The required inputs not yet given, in the order they appear on the page. */
export const detailsMissing = (details: FlowDetails): string[] => {
  // Every program (and every Assessment) is for roles; with groups, Read & Assess holds them.
  const audienceMissing = details.groups
    ? groupsMissing(details.groups)
    : details.roleIds.length > 0
    ? []
    : ['Audience'];
  return [
    !details.name.trim() && 'Name',
    !details.stageId && 'Stage',
    !details.category && 'Category',
    ...audienceMissing,
    !details.ownerId && 'Owner'
  ].filter((m): m is string => Boolean(m));
};

/** The bottom bar's status for a step: what is left, or that it is complete and what comes next. */
export const stepBar = (name: string, missing: string[], next = ''): { title: string; body: string; done: boolean } =>
  missing.length === 0
    ? { done: true, title: `${name} complete`, body: next }
    : {
        done: false,
        title: `${missing.length} required ${missing.length === 1 ? 'field' : 'fields'} left`,
        body: missing.join(', ')
      };
